import React from "react";
import { Container, Typography, Box, Divider, Link } from "@mui/material";

const PrivacyPolicy: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom align="center">
        InkBowl Privacy Notice
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ mb: 4 }}
      >
        Effective: November 17, 2025
      </Typography>

      <Divider sx={{ mb: 4 }} />

      <Box sx={{ "& > *": { mb: 3 } }}>
        <section>
          <Typography variant="h4" gutterBottom>
            Overview
          </Typography>
          <Typography variant="body1" paragraph>
            InkBowl (collectively, &quot;InkBowl,&quot; &quot;we,&quot;
            &quot;us,&quot; &quot;our&quot;) is a subsidiary of Capricorn Labs
            LLC and is the company responsible for providing the Services
            (defined below). InkBowl respects your privacy and is committed to
            protecting the personal data we hold about you. If you have
            questions, comments, or concerns about this Privacy Notice or our
            processing of personal data, please see the bottom of this Privacy
            Notice for information about how to contact us.
          </Typography>
          <Typography variant="body1" paragraph>
            This Privacy Notice explains our practices with respect to personal
            data we collect and process about you. This includes information we
            collect through, or in association with, our website located at
            www.inkbowl.com, our apps that we may provide, our products and
            services that we may offer from time to time via our website and/or
            related apps, our related social media sites, or otherwise through
            your interactions with us (the website, apps, products, services,
            and social media pages, collectively, the &quot;Services&quot;).
          </Typography>
          <Typography variant="body1" paragraph>
            Please review the following to understand how we process and
            safeguard personal data about you. By using any of our Services,
            whether by visiting our website or otherwise, and/or by voluntarily
            providing personal data to us, you acknowledge that you have read
            and understand the practices contained in this Privacy Notice. This
            Privacy Notice may be revised from time to time, so please ensure
            that you check this Privacy Notice periodically to remain fully
            informed.
          </Typography>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Personal Data We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            We collect information that identifies, relates to, describes, is
            reasonably capable of being associated with, or could reasonably be
            linked, directly or indirectly, with a particular consumer or
            household (&quot;personal data&quot;). In addition, we may collect
            data that is not identifiable to you or otherwise associated with
            you and is not personal data. To the extent this data is stored or
            associated with personal data, it will be treated as personal data;
            otherwise, the data is not subject to this notice.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            What Personal Data Do We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            The types of personal data we collect about you depends on your
            interactions with us and your use of the Services. In the past
            twelve (12) months, we have collected the below categories of
            personal data from our users:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body1" paragraph>
              <strong>Personal Identifiers:</strong> such as your name, email
              address, date of birth, or other similar identifiers.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Demographic and education-related information:</strong>{" "}
              including your age, country of origin, school, and level of study
              (high school, undergraduate, or graduate).
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Account and profile information:</strong> OneCreator
              account credentials (managed by our authentication provider),
              profile photo, pronouns, social media links, and any media/content
              you choose to post (e.g., images, text, videos).
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>User-generated content:</strong> content you submit or
              post on the Services.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Education-related content within EssayCraft:</strong>{" "}
              assignments you submit, grades (numerical) and written feedback
              provided by teachers. We do not collect &quot;formal&quot;
              education records that require institutional verification.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Transaction-related information:</strong> subscription
              tier, purchase history, and transaction metadata.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Internet or network activity information:</strong> IP
              address, device, and browser information collected by our
              authentication provider for security and account purposes.
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            We will not collect additional categories of personal data other
            than those categories listed above. If we intend to collect
            additional categories of personal data, we will provide you with a
            new notice at or before the time of collection.
          </Typography>
        </section>

        <section>
          <Typography variant="h5" gutterBottom>
            How We Use Your Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            We collect and process your personal data as part of our business
            operations, which include:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body1">
              Provide and operate the Services, including account registration
              and access to OneCreator-based features.
            </Typography>
            <Typography component="li" variant="body1">
              Process transactions and manage subscriptions (e.g., premium model
              access).
            </Typography>
            <Typography component="li" variant="body1">
              Personalize and improve the Services, including understanding
              demographics.
            </Typography>
            <Typography component="li" variant="body1">
              Support EssayCraft functionality, including enabling submissions
              and making teacher feedback available to you.
            </Typography>
            <Typography component="li" variant="body1">
              Communicate with you, including sending service-related emails and
              limited marketing communications about InkBowl Services.
            </Typography>
            <Typography component="li" variant="body1">
              Maintain safety, integrity, and security (e.g., account
              authentication, fraud prevention, debugging, error monitoring).
            </Typography>
            <Typography component="li" variant="body1">
              Comply with legal obligations and respond to lawful requests.
            </Typography>
            <Typography component="li" variant="body1">
              Enforce our terms, protect our rights, property, users, and the
              public.
            </Typography>
          </Box>
        </section>

        <section>
          <Typography variant="h5" gutterBottom>
            How We Obtain Your Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            We collect your personal data from the following categories of
            sources:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body1" paragraph>
              <strong>Directly from you:</strong> When you create an account,
              subscribe, purchase, post content, or otherwise interact with the
              Services.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>From our Service Providers:</strong> We may receive
              information from our service providers that is necessary for the
              processing of payments and for the operation and hosting of the
              Services. Our service providers, such as our authentication
              provider, may collect your IP address, device details and browser
              information to support security measures and manage login
              sessions.
            </Typography>
          </Box>
        </section>

        <section>
          <Typography variant="h5" gutterBottom>
            Who We Share Your Personal Data With
          </Typography>
          <Typography variant="body1" paragraph>
            We may share personal data when required by law, to fulfill our
            contractual obligations to you, or for other legitimate interests,
            to the following:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body1" paragraph>
              <strong>Our Service Providers:</strong> For example, IT service
              providers and cloud storage service providers, which may handle
              personal data confidentially on our behalf.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Our Affiliated Entities:</strong> MockVue AI and our
              parent company, Capricorn Labs LLC, to operate and support the
              Services.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>
                Government Agencies or other Competent Regulatory Authorities:
              </strong>{" "}
              When permitted or required to do so by law; in response to a
              request from a law enforcement agency or authority or any
              regulatory authority; and/or to protect the integrity of the
              Services or our interests, rights, property, or safety, and/or
              that of our users and others.
            </Typography>
          </Box>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Your Rights Regarding Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            You have certain rights regarding the collection and processing of
            personal data. You may exercise these rights, to the extent they
            apply to you, by contacting us at the information provided at the
            end of this Privacy Notice, or by following instructions provided in
            this Privacy Notice or in communications sent to you.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Accessing, Modifying, Rectifying, and Correcting Collected Personal
            Data
          </Typography>
          <Typography variant="body1" paragraph>
            We strive to maintain the accuracy of any personal data collected
            from you and will try to respond promptly to update our records when
            you tell us the information in our records is not correct. However,
            we must rely upon you to ensure that the information you provide us
            is complete, accurate, and up-to-date, and inform us of any changes.
            Please review all of your information carefully before submitting it
            to us and notify us as soon as possible of any updates or
            corrections.
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on the laws that apply to you, you may obtain from us
            certain personal data from you in our records. If you wish to
            access, review, or make any changes to personal data you have
            provided to us through the Services, please contact us at the
            information provided at the end of this Privacy Notice. We reserve
            the right to deny access as permitted or required by applicable law.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Your California Privacy Rights
          </Typography>
          <Typography variant="body1" paragraph>
            California&apos;s &quot;Shine the Light&quot; law permits our users
            who are California residents to request and obtain from us a list of
            what personal data (if any) we disclosed to third parties for their
            own direct marketing purposes in the previous calendar year and the
            names and addresses of those third parties. Requests may be made
            only once per year per person, they must be sent to the email
            address below and are free of charge. However, we do not disclose
            personal data protected under the &quot;Shine the Light&quot; law to
            third parties for their own direct marketing purposes.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Your Nevada Privacy Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Nevada law permits our users who are Nevada consumers to request
            that their personal data not be sold (as defined under applicable
            Nevada law), even if their personal data is not currently being
            sold. Requests may be sent to the email address below.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Your UK GDPR and EU GDPR Rights
          </Typography>
          <Typography variant="body1" paragraph>
            We offer our Services to individuals in the United Kingdom
            (&quot;UK&quot;) and the European Economic Area (&quot;EEA&quot;).
            As a result, our processing of personal data in connection with
            these services is subject to the UK General Data Protection
            Regulation (&quot;UK GDPR&quot;) and the European Union General Data
            Protection Regulation (&quot;EU GDPR&quot;). Under this Privacy
            Notice, InkBowl acts as the data controller of personal data, which
            means we are responsible for deciding how personal data about you is
            collected, held and used. As detailed above, InkBowl is a subsidiary
            of Capricorn Labs LLC.
          </Typography>
          <Typography variant="body1" paragraph>
            The UK GDPR and EU GDPR serve two primary purposes: (a) to establish
            rules for the collection, processing, and protection of personal
            data, and (b) to grant individuals specific rights regarding their
            personal data. Accordingly, in addition to the rights described
            above, individuals in the UK and the EEA enjoy enhanced rights under
            the UK GDPR and EU GDPR, as set out below:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body1" paragraph>
              <strong>Access:</strong> Request confirmation of whether we are
              processing your personal data and obtain a copy of the personal
              data we hold about you, along with details about the purposes of
              processing, categories of data processed, recipients of the data,
              retention periods and your rights related to that data.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Rectification:</strong> Request that we correct any
              inaccurate or incomplete personal data we hold about you without
              undue delay.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Erasure (&quot;Right to be Forgotten&quot;):</strong>{" "}
              Request the deletion of your personal data where it is no longer
              necessary for the purposes for which it was collected, where you
              have withdrawn consent, or where the processing is unlawful,
              subject to any overriding legal obligations.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Restriction of Processing:</strong> Request that we limit
              the processing of your personal data in certain circumstances,
              such as when you contest the accuracy of the data, the processing
              is unlawful but you oppose erasure, or where you have objected to
              processing pending verification.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Objection:</strong> Object to the processing of your
              personal data on grounds relating to your particular situation,
              including for direct marketing purposes, unless we demonstrate
              compelling legitimate grounds for the processing that override
              your interests.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Data Portability:</strong> Request that your personal
              data, which you have provided to us, be transferred to you or to
              another data controller in a structured, commonly used, and
              machine-readable format, where technically feasible.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Automated Decision-Making and Profiling:</strong> Request
              not to be subject to decisions based solely on automated
              processing, including profiling, which produce legal effects or
              similarly significant impacts on you, except where authorized by
              law or with your explicit consent.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Withdrawal of Consent:</strong> Withdraw your consent to
              data processing at any time where consent is the legal basis for
              processing, without affecting the lawfulness of processing before
              withdrawal. To withdraw your consent, please contact
              privacy@inkbowl.com.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Lodge a Complaint:</strong> File a complaint with a
              competent data protection supervisory authority if you believe
              that your rights under the UK GDPR or EU GDPR have been violated.
              In the UK, this authority is the Information Commissioner&apos;s
              Office. In the EEA, this is the relevant national data protection
              authority of your member state, a full list can be found{" "}
              <Link
                href="https://edpb.europa.eu/about-edpb/about-edpb/members_en"
                target="_blank"
                rel="noopener"
              >
                here
              </Link>
              .
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            In most cases, you will not be charged a fee to access your personal
            data or to exercise any of your other data protection rights.
            However, we may charge a reasonable fee if your request is clearly
            unfounded or excessive, or we may refuse to comply with the request
            in such circumstances.
          </Typography>
          <Typography variant="body1" paragraph>
            We may ask you to provide specific information to verify your
            identity and confirm your right to access the personal data (or to
            exercise any of your other rights). This is an appropriate security
            measure to ensure that personal data is not disclosed to anyone who
            is not entitled to receive it.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Canadian Privacy Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Residents of Canada are permitted to request and obtain from us
            information respecting the existence, use, and disclosure of their
            personal data as well as access to that information (subject to
            certain exceptions pursuant to applicable laws). Without limiting
            the above, residents of Canada will, upon request:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body1">
              Be informed of whether we hold personal data about you;
            </Typography>
            <Typography component="li" variant="body1">
              Be provided with an account of third parties to which we have
              disclosed your personal data;
            </Typography>
            <Typography component="li" variant="body1">
              Be able to challenge the accuracy and completeness of your
              personal data and have it amended as appropriate; and
            </Typography>
            <Typography component="li" variant="body1">
              Be provided with information about our policies and practices with
              respect to the management of personal data.
            </Typography>
          </Box>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Your Choices
          </Typography>
          <Typography variant="body1" paragraph>
            You have choices about certain information we collect about you, how
            we communicate with you, and how we process certain personal data.
            When you are asked to provide information, you may decline to do so;
            but if you choose not to provide information that is necessary to
            provide some of our Services, you may not be able to use those
            Services. In addition, it is possible to change your browser
            settings to block the automatic collection of certain information.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Communications Opt-Out
          </Typography>
          <Typography variant="body1" paragraph>
            You may opt out of receiving marketing or other communications from
            us at any time through a given communications channel (such as
            email) by following the opt-out link or other unsubscribe
            instructions provided in any email message received or by contacting
            us as provided at the end of this Privacy Notice. If you wish to opt
            out by sending us an email to the address provided below, please
            include &quot;Opt-Out&quot; in the email&apos;s subject line and
            include your name and the email address you used to sign up for
            communications in the body of the email. Note that if you do
            business with us in the future, you may not, subject to applicable
            law, opt out of certain automated notifications, such as order or
            subscription confirmations, based on business transactions (e.g.,
            e-commerce).
          </Typography>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Protecting Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            We use reasonable and appropriate technical and organizational
            safeguards designed to promote the security of our systems and
            protect the confidentiality, integrity, availability, and resilience
            of personal data. Those safeguards include, but are not limited to:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body1" paragraph>
              <strong>Hosting on Google Cloud:</strong> The Services are hosted
              on Google Cloud, which implements platform-level security measures
              such as encryption of data at rest.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Encryption in transit:</strong> All data transmitted to
              and from the Services is secured using HTTPS to ensure encryption
              during transmission.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Access controls and permissions management:</strong>{" "}
              Access to resources is controlled and managed via Google Identity
              and Access Management, enforcing strict permissions and access
              policies.
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            However, no method of safeguarding information is completely secure.
            While we use measures designed to protect personal data, we cannot
            guarantee that our safeguards will be effective or sufficient. In
            addition, you should be aware that Internet data transmission is not
            always secure, and we cannot warrant that information you transmit
            utilizing the Services is or will be secure.
          </Typography>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Retention of Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your personal data until your user profile is deleted as
            well as to the extent it is necessary to carry out the processing
            activities described above. The retention period for your personal
            data depends on the purpose for which it is processed. In general,
            we apply the following criteria:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body1" paragraph>
              <strong>Networking and community interactions:</strong> Retained
              for as long as your account is active. This includes private
              messages between users, connection requests and acceptance
              history, participation in public or private groups, comments,
              posts and other collaborative activity. If your account is closed,
              this data will be deleted within 3 years.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>Creative content:</strong> Retained for as long as you
              choose to keep it published or shared on the platform. This
              includes articles, essays, artwork, or other materials you submit,
              publish, or share in groups or communities. If you delete content,
              it will be removed from public view immediately and securely
              deleted from our systems within 3 years, unless retention is
              required by law.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              <strong>AI-assisted learning data:</strong> Retained for up to 3
              years to support your learning history and improve our Services.
              This includes your interactions with AI learning tools, usage
              logs, feedback you provide and other data generated while using
              AI-assisted features. After the retention period, this data will
              be deleted.
            </Typography>
          </Box>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Other Important Information About Personal Data and the Services
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Collection of Personal Data from Users Under 16
          </Typography>
          <Typography variant="body1" paragraph>
            The Services are intended for individuals aged 16 years of age or
            older. Children under 16 are not permitted to use the Services and
            we do not knowingly collect personal data from anyone under the age
            of 16. By using the Services, you represent that you are 16 years of
            age or older. If we become aware that we have collected personal
            data from a user under 16, we will take reasonable steps to delete
            such information from our systems in accordance with the UK GDPR and
            EU GDPR. If you are a parent or guardian and become aware that your
            child has provided us with personal data, please contact us at
            privacy@inkbowl.com. We implement data protection by design and by
            default to safeguard all personal data. This includes minimizing the
            collection of personal data and providing clear information about
            how personal data is used and data subjects&apos; rights.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Third-Party Websites and Services
          </Typography>
          <Typography variant="body1" paragraph>
            As a convenience, we may reference or provide links to third-party
            websites and services, including those of unaffiliated third
            parties, our affiliates, service providers, and third parties with
            which we do business. When you access these third-party services,
            you leave our Services, and we are not responsible for, and do not
            control, the content, security, or privacy practices employed by any
            third-party websites and services. You access these third-party
            services at your own risk. This Privacy Notice does not apply to any
            third-party services; please refer to the Privacy Notices or
            policies for such third-party services for information about how
            they collect, use, and process personal data.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Business Transfer
          </Typography>
          <Typography variant="body1" paragraph>
            We may, in the future, sell or otherwise transfer some or all of our
            business, operations or assets to a third party, whether by merger,
            acquisition or otherwise. Personal data we obtain from or about you
            via the Services may be disclosed to any potential or actual
            third-party acquirers and may be among those assets transferred.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Do Not Track
          </Typography>
          <Typography variant="body1" paragraph>
            We do not currently process or comply with any web browser&apos;s
            &quot;do not track&quot; signal or similar mechanisms. You may
            exercise your choice regarding the use of cookies and other tracking
            technologies by visiting the analytics systems websites for
            customization tools or by modifying the settings of your web
            browsers across the devices you use.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            International Use
          </Typography>
          <Typography variant="body1" paragraph>
            Your personal data will be stored and processed in the United
            States. If you are using the Services from outside the United
            States, by your use of the Services you acknowledge that we will
            transfer your data to, and store your personal data in, the United
            States, which may have different data protection rules than in your
            country, and personal data may become accessible as permitted by law
            in the United States, including to law enforcement and/or national
            security authorities in the United States.
          </Typography>
          <Typography variant="body1" paragraph>
            In connection with the purposes described above, we may transfer
            personal data of individuals in the UK and EEA to countries outside
            the UK and EEA, including jurisdictions that may not be recognized
            as providing an equivalent level of data protection. Where such data
            is transferred to and processed in the United States, we will
            implement appropriate safeguards to ensure compliance with
            applicable data protection laws.
          </Typography>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Modifications and Updates to this Privacy Notice
          </Typography>
          <Typography variant="body1" paragraph>
            This Privacy Notice replaces all previous disclosures we may have
            provided to you about our information practices with respect to the
            Services. We reserve the right, at any time, to modify, alter,
            and/or update this Privacy Notice, and any such modifications,
            alterations, or updates will be effective upon our posting of the
            revised Privacy Notice. We will use reasonable efforts to notify you
            in the event material changes are made to our processing activities
            and/or this Privacy Notice, such as by posting a notice on the
            Services. Your continued use of the Services following our posting
            of any revised Privacy Notice will constitute your acknowledgement
            of the amended Privacy Notice.
          </Typography>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Applicability of this Privacy Notice
          </Typography>
          <Typography variant="body1" paragraph>
            This Privacy Notice applies regardless of the means used to access
            or provide information through the Services. This Privacy Notice
            does not apply to information from or about you collected by any
            third-party services, applications, or advertisements associated
            with, or websites linked from the Services. The collection or
            receipt of your information by such third parties is subject to
            their own privacy policies, statements, and practices, and under no
            circumstances are we responsible or liable for any third
            party&apos;s compliance therewith.
          </Typography>
        </section>

        <section>
          <Typography variant="h4" gutterBottom>
            Additional Information and Assistance
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions or concerns about this Privacy Notice
            and/or how we process personal data and/or want more information
            about how users with disabilities can access this Privacy Notice in
            an alternative format, please contact us at{" "}
            <Link href="mailto:privacy@inkbowl.com">privacy@inkbowl.com</Link>.
          </Typography>
          <Typography variant="body1" paragraph>
            If you are located in the UK or the EEA and would like to raise a
            concern about how we handle your personal data, you also have the
            right to contact your local supervisory authority. A list of EEA
            authorities can be found{" "}
            <Link
              href="https://edpb.europa.eu/about-edpb/about-edpb/members_en"
              target="_blank"
              rel="noopener"
            >
              here
            </Link>
            . For the UK, the supervisory authority is the{" "}
            <Link
              href="https://ico.org.uk/make-a-complaint/"
              target="_blank"
              rel="noopener"
            >
              Information Commissioner&apos;s Office
            </Link>
            .
          </Typography>
        </section>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
